import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import {
  deriveLineAmount,
  convertOfferLineUnit,
  effectiveUnitPrice,
  hourlyBasisFromUnitPrice,
  normalizeOfferUnit,
  isOfferTimeUnit,
} from "@/lib/offer-calc";
import type { OfferLine } from "@/hooks/use-offers";

const UNIT_OPTIONS = ["t", "d", "stk", "fast"] as const;

interface Props {
  line: OfferLine | null;
  hourlyRate: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (patch: Partial<OfferLine>) => void;
  onDelete: () => void;
}

interface DraftFields {
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  rate_basis_hourly: number | null;
  purchase_price: number;
  comment: string;
}

/**
 * Mobile bottom-sheet for full redigering av en tilbudslinje. Åpnes når
 * brukeren tapper en OfferLineCard. Lagrer via onSave(patch) ved knapp-trykk.
 * Slett-knapp bekrefter før sletting.
 */
export function OfferLineEditSheet({
  line,
  hourlyRate,
  open,
  onOpenChange,
  onSave,
  onDelete,
}: Props) {
  const [draft, setDraft] = useState<DraftFields | null>(null);

  useEffect(() => {
    if (line && open) {
      setDraft({
        description: line.description,
        quantity: line.quantity,
        unit: normalizeOfferUnit(line.unit),
        unit_price: line.unit_price,
        rate_basis_hourly: line.rate_basis_hourly ?? null,
        purchase_price: line.purchase_price,
        comment: line.comment ?? "",
      });
    }
  }, [line, open]);

  if (!line || !draft) return null;

  // Auto-genererte linjer kan ikke redigeres helt (description/quantity)
  // — de styres av systemet (tilbudsskriving-credit).
  const isLocked = line.is_auto_generated ?? false;

  function applyUnit(nextRaw: string) {
    if (!draft || !line) return;
    const next = normalizeOfferUnit(nextRaw);
    const from = normalizeOfferUnit(draft.unit);
    if (
      line.cost_type === "internal" &&
      isOfferTimeUnit(from) &&
      isOfferTimeUnit(next)
    ) {
      const converted = convertOfferLineUnit({
        fromUnit: from,
        toUnit: next,
        quantity: draft.quantity,
        unitPrice: effectiveUnitPrice(from, draft.unit_price, hourlyRate),
        hourlyBasis: draft.rate_basis_hourly ?? hourlyRate,
      });
      setDraft({
        ...draft,
        unit: converted.unit,
        unit_price: converted.unit_price,
        rate_basis_hourly: converted.rate_basis_hourly,
      });
      return;
    }
    setDraft({ ...draft, unit: next });
  }

  function handleSave() {
    if (!line || !draft) return;
    const amount = deriveLineAmount(
      {
        cost_type: line.cost_type,
        pricing_type: line.pricing_type,
        quantity: draft.quantity,
        unit: draft.unit,
        unit_price: draft.unit_price,
      },
      hourlyRate,
    );
    onSave({
      description: draft.description,
      quantity: draft.quantity,
      unit: draft.unit,
      unit_price: draft.unit_price,
      // VIB-2097: på t/d er satsen tidsbasisen; på fast/stk beholdes lagret.
      rate_basis_hourly:
        hourlyBasisFromUnitPrice(draft.unit, draft.unit_price) ?? draft.rate_basis_hourly,
      purchase_price: draft.purchase_price,
      comment: draft.comment.trim() || null,
      amount,
    });
    onOpenChange(false);
  }

  function handleDelete() {
    if (!line) return;
    if (!window.confirm(`Slett "${line.description}"?`)) return;
    onDelete();
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Rediger linje</SheetTitle>
        </SheetHeader>
        <div className="space-y-3 mt-4">
          <div>
            <Label className="text-xs">Beskrivelse</Label>
            <Input
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              disabled={isLocked}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Mengde</Label>
              <Input
                type="number"
                step="0.25"
                value={String(draft.quantity)}
                onChange={(e) =>
                  setDraft({ ...draft, quantity: Number(e.target.value) || 0 })
                }
                disabled={isLocked}
                className="tabular-nums"
              />
            </div>
            <div>
              <Label className="text-xs">Enhet</Label>
              <Select
                value={draft.unit}
                onValueChange={applyUnit}
                disabled={isLocked}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_OPTIONS.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Sats</Label>
              <Input
                type="number"
                step="50"
                value={String(draft.unit_price)}
                onChange={(e) =>
                  setDraft({ ...draft, unit_price: Number(e.target.value) || 0 })
                }
                className="tabular-nums"
              />
            </div>
            <div>
              <Label className="text-xs">Innkjøp</Label>
              <Input
                type="number"
                step="50"
                value={String(draft.purchase_price)}
                onChange={(e) =>
                  setDraft({ ...draft, purchase_price: Number(e.target.value) || 0 })
                }
                className="tabular-nums"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Kommentar (valgfritt)</Label>
            <Textarea
              value={draft.comment}
              onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
              rows={2}
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t">
          {!isLocked ? (
            <Button
              variant="ghost"
              onClick={handleDelete}
              className="text-destructive hover:text-destructive"
            >
              <SrIcon name="trash2" className="w-4 h-4 mr-1.5" />
              Slett
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">
              Auto-generert linje — kun delvis redigerbar
            </span>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button onClick={handleSave}>Lagre</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
